import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Users, BrainCircuit, Phone, PhoneOff, MessageSquare, CheckCircle2, AlertTriangle, Search, Send, FileText, Clock, Zap } from "lucide-react";

const typeIcon = {
  reason: BrainCircuit,
  action: CheckCircle2,
  call: Phone,
  call_end: PhoneOff,
  sms: MessageSquare,
  check: Search,
  send: Send,
  file: FileText,
  wait: Clock,
  warn: AlertTriangle,
  result: Zap,
};

const typeStyle = {
  reason: "bg-blue-50 border-blue-100 text-blue-700",
  action: "text-slate-700",
  call: "bg-emerald-50 border-emerald-100 text-emerald-700",
  call_end: "text-slate-500",
  sms: "bg-sky-50 border-sky-100 text-sky-700",
  check: "text-slate-600",
  send: "text-slate-600",
  file: "text-slate-600",
  wait: "text-amber-600",
  warn: "bg-amber-50 border-amber-100 text-amber-700",
  result: "bg-emerald-50 border-emerald-100 text-emerald-700 font-semibold",
};

/* ─── All AI Functions ───
   agent field = which of the 20 AI products owns this function (for future mapping)
   NOT displayed on this slide */
const allFunctions = [

  // ── AI Receptionist ──
  { agent: "AI Receptionist", text: "Answer & Triage Inbound Calls 24/7", workflow: [
    { type: "action", text: "Incoming call detected — picks up within 2 rings, matches caller ID against patient database" },
    { type: "check", text: "Pulling full patient record: history, insurance, balance, open treatment plans" },
    { type: "reason", text: "Identifies caller intent from first sentence — scheduling, billing, emergency, refill request" },
    { type: "reason", text: "Adapts tone based on caller's emotional state — rushed, anxious, confused, frustrated" },
    { type: "call", text: "\"Hi Sarah! I see you're due for your crown on #14 — is that what you're calling about?\"" },
    { type: "action", text: "Routes to correct workflow — booking, triage, transfer, or answers question directly" },
    { type: "result", text: "Every call answered 24/7/365 — caller identified, intent classified, routed — no hold music, no voicemail" },
  ]},
  { agent: "AI Receptionist", text: "Live Emergency Triage on Phone", workflow: [
    { type: "call", text: "Caller sounds distressed — \"I have really bad pain, my face is swelling, I can barely open my mouth\"" },
    { type: "reason", text: "Swelling + trismus + 3 days worsening = potential abscess. Assessing: fever? difficulty swallowing? breathing?" },
    { type: "warn", text: "Fever 101°F + mild dysphagia = ELEVATED risk — possible fascial space involvement. Must be seen today" },
    { type: "check", text: "Scanning today's schedule — Dr. Patel fully booked. Found moveable 2 PM consult (patient is flexible)" },
    { type: "action", text: "Moved consult to Thursday (patient approved via text). 60-min emergency slot created" },
    { type: "call", text: "\"I've opened a 2 PM slot today with Dr. Patel. He's excellent with emergencies.\"" },
    { type: "result", text: "Emergency triaged, severity assessed, schedule rearranged, patient booked — all while on the phone" },
  ]},
  { agent: "AI Receptionist", text: "Book Appointments During the Call", workflow: [
    { type: "reason", text: "Determining procedure type, required provider, operatory, equipment, and time block needed" },
    { type: "check", text: "Cross-referencing provider schedules, operatory availability, and patient preferences" },
    { type: "action", text: "Running real-time eligibility check — insurance active, emergency visit covered at 100%" },
    { type: "warn", text: "Practice is out-of-network. Patient OOP could be $200-400. Checking emergency discount..." },
    { type: "call", text: "\"We have a $99 new patient emergency special, and payment plans for any treatment needed.\"" },
    { type: "action", text: "Appointment created in PMS — procedure codes, provider, operatory, clinical alerts all set" },
    { type: "sms", text: "Sending confirmation with date, time, directions, pre-visit checklist, and calendar invite" },
    { type: "result", text: "Insurance verified, cost quoted, appointment booked, confirmation sent — no callbacks needed" },
  ]},

  // ── AI Schedule Optimizer ──
  { agent: "AI Schedule Optimizer", text: "Fill Cancellation Gaps in Minutes", workflow: [
    { type: "warn", text: "3 cancellations hit — $4,500 implant, $890 SRP, $1,340 crown. Total $6,730 at risk" },
    { type: "reason", text: "Ranking gaps by revenue impact. Implant first ($4,500), then crown ($1,340), then SRP ($890)" },
    { type: "check", text: "Querying waitlist: matching treatment type, provider, insurance status, time preferences" },
    { type: "sms", text: "Texting top candidates simultaneously — \"We have an opening on [date/time]. Want it? Reply YES\"" },
    { type: "call", text: "Calling candidates who haven't responded — offers opening, checks insurance, quotes OOP on the spot" },
    { type: "action", text: "First YES wins — booking into PMS, sending confirmation, updating waitlist" },
    { type: "result", text: "3 gaps filled: 2 phone calls, 4 texts — $6,285 of $6,730 recovered in 12 minutes" },
  ]},
  { agent: "AI Schedule Optimizer", text: "Optimize Daily Schedule for Max Production", workflow: [
    { type: "check", text: "Scanning full schedule across all providers — found 2.5 hours of dead time, 3 inefficient blocks" },
    { type: "reason", text: "Dr. Patel: 45-min gap at 11 AM. Dr. Kim: 60-min gap at 1 PM (chronic no-show patient)" },
    { type: "reason", text: "Strategy: move 2:30 filling → 11 AM gap. Pull waitlist patient into freed 2 PM block" },
    { type: "call", text: "\"Mrs. Thompson, we have an earlier slot at 11 AM — would that work better for you?\"" },
    { type: "action", text: "Double-booked Dr. Kim's 1 PM (chronic no-show) with backup exam from waitlist" },
    { type: "action", text: "Balancing hygiene vs. restorative blocks to maximize production per hour" },
    { type: "result", text: "Recovered 2.5 hrs of dead time — 4 calls, 6 texts, 8 PMS updates — all autonomous" },
  ]},
  { agent: "AI Schedule Optimizer", text: "Confirm Appointments & Handle Responses", workflow: [
    { type: "reason", text: "18 appointments tomorrow — scoring each by no-show risk. 4 patients flagged high-risk" },
    { type: "sms", text: "Batch texting 14 low-risk patients: \"Confirming your appointment tomorrow at [time]. Reply C to confirm.\"" },
    { type: "call", text: "Calling 4 high-risk patients individually — natural confirmation conversation" },
    { type: "call", text: "\"Hi Robert, confirming your cleaning tomorrow at 10 AM — will you be able to make it?\"" },
    { type: "reason", text: "Robert hesitant — pivoting to rescheduling. Finding 3 alternative times this week" },
    { type: "action", text: "If patient cancels, immediately triggering waitlist fill workflow for the opened slot" },
    { type: "wait", text: "Retrying non-responders on escalating cadence until appointment time" },
    { type: "result", text: "16 of 18 confirmed. 1 rescheduled (saved). 1 pending — follow-up queued. Zero staff time." },
  ]},

  // ── AI Hygiene Recall ──
  { agent: "AI Hygiene Recall", text: "Systematic Recall of Overdue Patients", workflow: [
    { type: "check", text: "Scanning entire database — 847 patients overdue for hygiene (6+ months since last cleaning)" },
    { type: "action", text: "Segmenting: 0-2 months overdue (312), 3-6 months (289), 6-12 months (178), 12+ months (68)" },
    { type: "sms", text: "Batch texting recent overdue: \"You're due for your cleaning! Book now: [link]\"" },
    { type: "call", text: "Calling 6-12 month patients — personal conversations about why they should come back" },
    { type: "reason", text: "Mrs. Davis worried about cost — checking insurance: 2 cleanings covered at 100%, 1 remaining" },
    { type: "call", text: "\"Great news — your cleaning is fully covered! How's Thursday at 2 PM?\"" },
    { type: "sms", text: "Non-responders enter drip campaign — weekly touchpoints for 4 weeks before marking inactive" },
    { type: "result", text: "847 patients worked: 234 scheduled, 89 promised to call back, 456 in drip sequence. Zero staff time." },
  ]},
  { agent: "AI Hygiene Recall", text: "Reactivate Dormant Patients (12+ Months)", workflow: [
    { type: "check", text: "68 patients inactive 12+ months — $34K in potential hygiene revenue sitting dormant" },
    { type: "reason", text: "Analyzing each patient's history for personalized re-engagement angle" },
    { type: "call", text: "\"Hi Robert, we haven't seen you in a while. You had some early periodontal concerns — I want to make sure those haven't progressed.\"" },
    { type: "reason", text: "Robert ghosted after a painful SRP. Pivoting to empathy — acknowledging the experience, offering different hygienist" },
    { type: "call", text: "\"I completely understand. We have a new hygienist who specializes in gentle cleanings, and we can use topical numbing...\"" },
    { type: "action", text: "Robert agreed — booked with comfort protocol notes: topical numbing, slower pace, frequent check-ins" },
    { type: "result", text: "68 dormant patients worked: 12 reactivated, 8 confirmed lost (reason logged), 48 in drip campaign" },
  ]},

  // ── AI Patient Experience ──
  { agent: "AI Patient Experience", text: "Post-Visit Surveys & Google Review Generation", workflow: [
    { type: "sms", text: "Sending post-visit survey 2 hours after appointment: \"How was your visit? Rate 1-5\"" },
    { type: "check", text: "Today's results: 12 responses — 9 rated 5/5, 2 rated 4/5, 1 rated 2/5" },
    { type: "action", text: "9 five-star patients → sending Google Review request with direct link" },
    { type: "warn", text: "1 two-star patient (Mrs. Patterson): \"Waited 40 minutes\" — escalating immediately" },
    { type: "reason", text: "Service recovery opportunity — drafting personalized apology for manager approval" },
    { type: "sms", text: "After approval: \"We sincerely apologize for the wait. We'd like to offer complimentary whitening...\"" },
    { type: "result", text: "4 new Google reviews generated, 1 unhappy patient caught before negative review posted" },
  ]},
  { agent: "AI Patient Experience", text: "Real-Time Reputation Defense", workflow: [
    { type: "warn", text: "ALERT: 1-star Google review just posted — Lisa Chen, left from the parking lot" },
    { type: "check", text: "Review says: \"Charged $340 for what was supposed to be $87. Complete ripoff.\"" },
    { type: "reason", text: "Investigating — pulling Lisa's ledger, today's procedure, and insurance EOB..." },
    { type: "check", text: "Found it: front desk quoted preventive rate ($87) but procedure was restorative ($340 copay)" },
    { type: "action", text: "This was a misquote — patient was given wrong information. Processing $253 refund" },
    { type: "warn", text: "Escalating to office manager: front desk needs retraining on preventive vs. restorative quoting" },
    { type: "sms", text: "Calling Lisa to apologize, explain the error, confirm refund processed" },
    { type: "result", text: "Root cause identified, refund processed, patient contacted — review updated to 4 stars in 14 minutes" },
  ]},

  // ── AI Case Acceptance ──
  { agent: "AI Case Acceptance", text: "Navigate Financial Objections & Close Cases", workflow: [
    { type: "call", text: "Margaret, 72, retired teacher: \"I just can't afford $12,400. I'm on a fixed income.\"" },
    { type: "reason", text: "Breaking down treatment: $5,200 urgent (crowns), $3,800 needed soon (decay), $3,400 elective (implant)" },
    { type: "check", text: "Checking programs — Senior Care discount eligible: 15% off = $10,540. Still too high for fixed income" },
    { type: "reason", text: "Phasing strategy: Phase 1 urgent crowns only ($2,240 after discount) + temporary partial ($680) instead of implant" },
    { type: "action", text: "6-month payment plan: $430/month. Phase 2 & 3 after benefits reset in January" },
    { type: "call", text: "\"We can start with just the two urgent crowns and a temporary — $430/month for 6 months. The rest can wait.\"" },
    { type: "result", text: "Tearful refusal → enthusiastic booking. $2,920 case accepted, $9,480 phased for next year" },
  ]},
  { agent: "AI Case Acceptance", text: "Unscheduled Treatment Follow-Up", workflow: [
    { type: "check", text: "Pulling all diagnosed-but-unscheduled treatment — 89 patients, $187K in pending production" },
    { type: "reason", text: "Prioritizing by: clinical urgency, dollar value, insurance year-end proximity" },
    { type: "call", text: "Calling high-value patients: \"You have $1,400 in benefits expiring Dec 31 — your crown would cost just $220 OOP before year-end\"" },
    { type: "reason", text: "Patient cites fear — discussing sedation options, comfort amenities, gentle approach" },
    { type: "action", text: "Patient cites cost — pulling insurance, calculating true OOP, presenting CareCredit at $94/month" },
    { type: "action", text: "Patient agrees — booking appointment, linking to treatment plan in PMS" },
    { type: "result", text: "$187K in unscheduled treatment systematically pursued — objections overcome, appointments booked" },
  ]},

  // ── AI Patient Intake ──
  { agent: "AI Patient Intake", text: "Send, Track & Complete Digital Intake Forms", workflow: [
    { type: "check", text: "New patient in 3 days — requires: medical history, dental history, insurance, consent, HIPAA" },
    { type: "sms", text: "\"Welcome to Riverside Dental! Please complete your new patient forms: [link]\"" },
    { type: "wait", text: "Monitoring completion — 3 of 5 forms done. Dental history and insurance still pending" },
    { type: "sms", text: "24-hour reminder: \"Just 2 more forms to go! Complete them now so we can get you right in\"" },
    { type: "warn", text: "48 hours before appointment — insurance form still incomplete" },
    { type: "call", text: "Calling patient — capturing insurance ID, group #, subscriber info verbally" },
    { type: "result", text: "All 5 forms completed before arrival — zero front-desk bottleneck at check-in" },
  ]},
  { agent: "AI Patient Intake", text: "Flag Medically Complex Patients", workflow: [
    { type: "check", text: "Harold, 81 — intake form shows: pacemaker, atrial fib, diabetes, 8 medications including warfarin + aspirin" },
    { type: "warn", text: "CRITICAL: Pacemaker — no ultrasonic scaler, no electrosurgery, no apex locator" },
    { type: "warn", text: "HIGH BLEEDING RISK: Warfarin + aspirin combination — INR check needed before invasive procedures" },
    { type: "reason", text: "Metoprolol (beta-blocker) + epinephrine = limit to 2 carpules of 1:100K epi maximum" },
    { type: "action", text: "Setting 3 clinical alerts in PMS: pacemaker protocol, bleeding risk, epi limitation" },
    { type: "action", text: "Flagging latex allergy — latex-free gloves and equipment for all appointments" },
    { type: "result", text: "All contraindications identified, clinical alerts set — provider sees everything before touching the patient" },
  ]},

  // ── AI Practice Manager ──
  { agent: "AI Practice Manager", text: "Morning Diagnostic Across All AI Agents", workflow: [
    { type: "check", text: "Running morning health check across all 20 AI agents — scanning for anomalies, failures, bottlenecks" },
    { type: "warn", text: "Issue 1: Denial appeal agent submitted 3 claims with WRONG patient demographics — HIPAA violation risk" },
    { type: "warn", text: "Issue 2: Cigna portal down — 15 patients need insurance verification for today's appointments" },
    { type: "warn", text: "Issue 3: Patient threatening legal action over billing dispute" },
    { type: "reason", text: "Prioritizing by impact: HIPAA violation (critical) > legal threat (high) > portal outage (medium)" },
    { type: "action", text: "Recalling 3 appeals immediately, notifying Cigna, deploying manual phone verification workaround" },
    { type: "result", text: "All 3 crises triaged, root causes identified, remediations in progress — before the office even opens" },
  ]},
  { agent: "AI Practice Manager", text: "Identify Tasks & Assign to Team or AI", workflow: [
    { type: "check", text: "Scanning today's workflow: 47 pending tasks across scheduling, billing, clinical, and admin" },
    { type: "reason", text: "Categorizing: 31 tasks AI can handle autonomously, 12 need staff action, 4 need doctor decision" },
    { type: "action", text: "Auto-assigning: 8 insurance verifications → AI Insurance Verification, 6 claim submissions → AI Claims" },
    { type: "action", text: "Flagging for office manager: 2 patient complaints, 1 staff scheduling conflict" },
    { type: "action", text: "Flagging for doctor: 1 complex treatment plan approval, 3 referral decisions" },
    { type: "result", text: "47 tasks triaged: 31 handled by AI automatically, 16 routed to the right human with full context" },
  ]},
  { agent: "AI Practice Manager", text: "Detect & Patch Systemic Failures", workflow: [
    { type: "warn", text: "Pattern detected: 3 denial appeals submitted with wrong patient demographics this week" },
    { type: "reason", text: "Root cause analysis — tracing back to database join: patient_id mapping error in appeal generation" },
    { type: "check", text: "Checking all appeals from past 30 days — found 7 more potentially affected submissions" },
    { type: "action", text: "Recalling all affected appeals before payers process them — HIPAA exposure contained" },
    { type: "action", text: "Patching the patient_id mapping logic — testing against 100 historical cases to verify fix" },
    { type: "warn", text: "Escalating to office manager with full incident report: what happened, impact, fix, prevention" },
    { type: "result", text: "Systemic bug caught, 10 bad appeals recalled, root cause fixed — before any HIPAA complaint" },
  ]},

  // ── AI Fax Manager ──
  { agent: "AI Fax Manager", text: "Receive, Classify & Route Inbound Faxes", workflow: [
    { type: "check", text: "14 faxes received today — OCR scanning and classifying each document" },
    { type: "action", text: "Fax 1-4: EOBs from Delta Dental — routing to EOB posting workflow, matching to patient claims" },
    { type: "action", text: "Fax 5: Referral from endodontist — attaching to patient chart, notifying treatment coordinator" },
    { type: "action", text: "Fax 6-8: Lab results — matching to patients by DOB/name, filing in records" },
    { type: "action", text: "Fax 9: Pre-auth approval from Cigna — updating treatment plan status, notifying front desk" },
    { type: "warn", text: "Fax 10: Illegible document — flagging for manual review with best-guess classification" },
    { type: "result", text: "13 of 14 faxes auto-classified, routed, and filed. 1 flagged for human. 45-min task done in 2" },
  ]},
  { agent: "AI Fax Manager", text: "Send Outbound Fax Packages", workflow: [
    { type: "check", text: "3 outbound fax packages queued — referral, appeal packet, records request response" },
    { type: "file", text: "Referral: compiling letter + clinical notes + X-rays + treatment history into single package" },
    { type: "send", text: "Faxing referral to endodontist — delivery confirmation received" },
    { type: "file", text: "Appeal: assembling denial letter + clinical narrative + perio charts + radiographs + ADA guidelines" },
    { type: "send", text: "Faxing appeal packet to MetLife — 12 pages transmitted, confirmation received" },
    { type: "warn", text: "Records request fax failed — retrying on alternate number from provider directory" },
    { type: "result", text: "3 packages sent, all confirmed delivered — supporting docs compiled and transmitted autonomously" },
  ]},

  // ── AI Insurance Verification ──
  { agent: "AI Insurance Verification", text: "Verify Eligibility & Benefits in Real-Time", workflow: [
    { type: "action", text: "Submitting 270/271 eligibility request to Delta Dental — response in 3 seconds" },
    { type: "check", text: "Plan ACTIVE. Annual max: $1,500. Used YTD: $320. Remaining: $1,180" },
    { type: "check", text: "Deductible: $50 (met). Preventive: 100%. Basic: 80%. Major: 50%" },
    { type: "check", text: "Frequency limits: BWX every 6 months (last: 4 months ago — eligible in 2 months)" },
    { type: "action", text: "Auto-updating PMS benefit tables with all coverage percentages and limits" },
    { type: "reason", text: "Crown (#14) at 50% = ~$670 OOP. Checking waiting period... None. Good to go" },
    { type: "result", text: "Full eligibility verified, benefits mapped, frequency limits tracked, OOP estimated — in seconds" },
  ]},
  { agent: "AI Insurance Verification", text: "Navigate Complex Multi-Payer COB", workflow: [
    { type: "check", text: "Diana has 3 insurance plans: Aetna (primary), BCBS (secondary), Medicaid (tertiary)" },
    { type: "reason", text: "Running COB rules: Birthday Rule for commercial plans, Medicaid always pays last" },
    { type: "warn", text: "Aetna has implant EXCLUSION RIDER — pays $0 on D6010. This changes everything" },
    { type: "reason", text: "When primary excludes, secondary can elevate to primary position. Checking BCBS..." },
    { type: "check", text: "BCBS steps up: covers implant at 50%, max $2,050. Medicaid: implants not covered" },
    { type: "reason", text: "Creative path: bone graft may qualify for medical crossover (CPT 21210) — checking Aetna medical..." },
    { type: "result", text: "Triple-insurance puzzle solved: patient cost reduced from $5,840 to ~$2,590 via COB + medical crossover" },
  ]},
  { agent: "AI Insurance Verification", text: "Log Into Payer Portals for Benefits Lookup", workflow: [
    { type: "action", text: "Logging into Delta Dental provider portal — navigating to member eligibility lookup" },
    { type: "check", text: "Searching by member ID + DOB — pulling full benefit summary page" },
    { type: "check", text: "Extracting: annual max ($1,500), deductible ($50 met), coverage tiers by CDT category" },
    { type: "check", text: "Downloading frequency limitation table — BWX, pano, prophy, SRP, crown replacement periods" },
    { type: "check", text: "Pulling claim history from portal — checking what's been used YTD, remaining balance" },
    { type: "reason", text: "Portal shows D2740 crown covered at 50% but with alternate benefit clause — downgrades PFM to metal. Flagging." },
    { type: "action", text: "Updating PMS benefit tables with portal-verified data — more detailed than electronic 270/271" },
    { type: "result", text: "Full portal-level benefits extracted: CDT-specific coverage, downgrades, frequency limits, claim history — no staff login needed" },
  ]},

  // ── AI Insurance Calls ──
  { agent: "AI Insurance Calls", text: "Call Insurance for Patient Benefits", workflow: [
    { type: "reason", text: "Electronic eligibility only returned basic tiers. Need CDT-level coverage for crown D2740" },
    { type: "call", text: "Calling Delta Dental provider line — navigating IVR menu system..." },
    { type: "wait", text: "On hold — 35 minutes. AI holds the line so your staff doesn't have to" },
    { type: "call", text: "Connected to rep. Verifying D2740 crown #14 — covered at 50%, no downgrade clause" },
    { type: "check", text: "Asking about missing tooth clause, waiting periods, posterior composite downgrades" },
    { type: "action", text: "Documenting every detail with reference #, rep name, date, exact responses" },
    { type: "file", text: "Updating PMS with CDT-specific coverage, limitations, and call reference number" },
    { type: "result", text: "42-minute call completed autonomously — detailed breakdown that would take your staff an hour+" },
  ]},
  { agent: "AI Insurance Calls", text: "Follow Up on Pending Claims & Appeals", workflow: [
    { type: "check", text: "5 claims pending 30+ days, 2 appeals with no response in 14 days — calling payers" },
    { type: "call", text: "Claim 1: $1,840 crown — MetLife, 67 days. Calling... reviewer needs updated X-ray" },
    { type: "action", text: "Uploading X-ray to MetLife portal immediately while still on the line" },
    { type: "call", text: "Appeal 1: SRP denial — Aetna, 21 days. Calling... adjudicator reviewing, decision by Friday" },
    { type: "call", text: "Claim 3: $450 filling — Cigna, 45 days. Status: 'Paid' but payment not received. Requesting EFT trace" },
    { type: "result", text: "5 claims + 2 appeals worked: 2 resolved on the call, 3 escalated, 2 pending decisions — all documented" },
  ]},

  // ── AI Claim Submission ──
  { agent: "AI Claim Submission", text: "Generate Clean Claims from Clinical Notes", workflow: [
    { type: "action", text: "Converting today's clinical notes into CMS-1500 claim format automatically" },
    { type: "action", text: "Mapping procedures: D2740 (#14, $1,180), D2950 (#14, $285) — total: $1,465" },
    { type: "check", text: "Applying correct modifiers — verifying against payer-specific requirements" },
    { type: "action", text: "Attaching clinical narrative, linking pre-authorization number, adding supporting X-rays" },
    { type: "check", text: "Verifying NPI, tax ID, place of service, rendering provider — all correct" },
    { type: "result", text: "Clean claim generated with all supporting docs, narratives, and pre-auth — ready for scrub" },
  ]},
  { agent: "AI Claim Submission", text: "Submit Claims & Track Through Adjudication", workflow: [
    { type: "check", text: "7 claims scrubbed and validated — all clear for submission" },
    { type: "send", text: "Submitting batch via clearinghouse — tracking numbers generated for each" },
    { type: "check", text: "Clearinghouse 999 acknowledgment: 7 of 7 accepted for processing" },
    { type: "action", text: "Setting automated status checks: 7 days, 14 days, 21 days, 30 days" },
    { type: "warn", text: "Day 14: Claim #3 shows 'pending additional info' — pulling and submitting requested docs" },
    { type: "result", text: "All claims submitted, confirmed, and tracked through adjudication — delays caught proactively" },
  ]},

  // ── AI Claim Scrubbing ──
  { agent: "AI Claim Scrubbing", text: "Scrub Claims Against 47+ Payer Rules", workflow: [
    { type: "check", text: "Running claim through 47-point payer-specific validation: bundling, frequency, tooth eligibility" },
    { type: "check", text: "Bundling check: D2740 + D2950 — not bundled by Delta Dental PPO. OK" },
    { type: "check", text: "Frequency check: last crown on #14 was 2019 — past 5-year replacement rule. OK" },
    { type: "check", text: "Fee schedule validation: $1,180 matches Delta PPO contracted rate. OK" },
    { type: "warn", text: "Pre-auth was for crown only. Buildup ($285) not on pre-auth — flagging for review" },
    { type: "reason", text: "Delta typically doesn't require separate pre-auth for buildups with crowns. Proceeding with note" },
    { type: "result", text: "Claim passed all 47 checks — 1 flag noted. Estimated first-pass acceptance: 97%" },
  ]},
  { agent: "AI Claim Scrubbing", text: "Auto-Fix Coding Errors Before Submission", workflow: [
    { type: "warn", text: "Scanning 12 claims for today — found 3 errors that would cause denials" },
    { type: "check", text: "Claim #4: D2392 missing distal surface designation — adding 'DO' based on clinical note" },
    { type: "check", text: "Claim #7: D4341 needs ICD-10 code — adding K05.311 (chronic periodontitis, localized, moderate)" },
    { type: "warn", text: "Claim #9: Composite billed at $310 but Aetna contracted rate is $245 — adjusting to prevent rejection" },
    { type: "action", text: "All 3 errors corrected automatically — no staff review needed for these standard fixes" },
    { type: "result", text: "3 claims saved from denial before they were submitted — coding errors caught and fixed in seconds" },
  ]},

  // ── AI Denial Management ──
  { agent: "AI Denial Management", text: "Analyze & Build Appeals for Denied Claims", workflow: [
    { type: "warn", text: "Claim denied: D4341 SRP — reason code CO-50 \"not medically necessary\"" },
    { type: "reason", text: "Common SRP denial. Need to prove necessity: perio charting showing 4mm+ pockets + BOP" },
    { type: "check", text: "Pulling perio chart — 14 sites with 5-6mm pockets, bleeding on probing, moderate bone loss" },
    { type: "file", text: "Drafting appeal: \"Patient presents with generalized moderate chronic periodontitis, AAP Stage II Grade B...\"" },
    { type: "action", text: "Attaching: perio chart, 4 BWX radiographs, intraoral photos, AAP clinical guidelines" },
    { type: "send", text: "Appeal submitted via payer portal — tracking number APL-2024-5529" },
    { type: "check", text: "Day 12: Appeal approved. SRP reprocessed and paid at 80% = $712" },
    { type: "result", text: "$712 recovered from a denial most offices write off. Clinical case built entirely by AI" },
  ]},
  { agent: "AI Denial Management", text: "Fight Wrongful Denials with Precedent", workflow: [
    { type: "warn", text: "Aetna denies D6010 implant — CO-29 'filing deadline expired.' But we filed on time." },
    { type: "reason", text: "Investigating: Aetna used Stage 2 uncovering date (11/02) instead of placement date (01/10) as DOS" },
    { type: "check", text: "ADA CDT 2024 is clear: D6010 DOS = placement date, not uncovering. Aetna is wrong." },
    { type: "action", text: "Pulling EDI transmission log — 999/277CA acknowledgment proves timely filing on 02/15" },
    { type: "check", text: "Found precedent: identical issue won 3 months ago with same Aetna adjudicator unit" },
    { type: "file", text: "Appeal citing: ADA CDT 2024, HIPAA §162.1002, EDI logs, operative note, prior case precedent" },
    { type: "result", text: "Wrongful denial overturned — bulletproof appeal with regulatory citations and prior win. 96% confidence." },
  ]},

  // ── AI Patient Billing ──
  { agent: "AI Patient Billing", text: "Multi-Channel Collection Outreach", workflow: [
    { type: "check", text: "34 patients with outstanding balances: 0-30 days ($4,200), 31-60 ($3,840), 61-90 ($2,400), 90+ ($2,400)" },
    { type: "sms", text: "Batch texting 0-30 day patients: friendly reminder with one-tap text-to-pay link" },
    { type: "call", text: "Calling 60+ day patients: \"I'm calling about a $420 balance from your February visit...\"" },
    { type: "action", text: "Mr. Park: \"I thought insurance covered that\" — pulling EOB, explaining 20% coinsurance" },
    { type: "action", text: "Offering 3-month plan: $140/month. Accepted — setting up auto-pay" },
    { type: "call", text: "90+ day patient — offering 10% courtesy discount to resolve today. Accepted — $1,080 collected" },
    { type: "result", text: "9 calls, 12 texts — $3,200 collected today, $1,680 on payment plans. All autonomous" },
  ]},
  { agent: "AI Patient Billing", text: "Navigate Sensitive Financial Situations", workflow: [
    { type: "call", text: "Maria Santos: $2,400 across family, 60 days overdue. Card declined. Front desk notes: husband moving out" },
    { type: "reason", text: "Tone shift — this isn't a collection call anymore. Detecting financial crisis signals" },
    { type: "call", text: "\"Maria, I can hear this is a difficult time. Let me see what options we have to make this easier.\"" },
    { type: "check", text: "Hardship program eligible — 40% reduction = $1,440. 12-month plan = $120/month" },
    { type: "reason", text: "Pending divorce may mean insurance change — providing COBRA information proactively" },
    { type: "action", text: "Enrolling in hardship program, setting up $120/month plan, sending COBRA details" },
    { type: "result", text: "At-risk family retained — went from declined card to grateful patient on a manageable plan" },
  ]},

  // ── AI AR & Collections ──
  { agent: "AI AR & Collections", text: "Work Aging AR by Priority & Dollar Value", workflow: [
    { type: "check", text: "Pulling aging AR: $42,300 outstanding. 8 claims over $500 in 60+ day bucket = $6,200 at risk" },
    { type: "reason", text: "Prioritizing by: dollar value × days outstanding. $1,840 MetLife crown at 67 days = top priority" },
    { type: "call", text: "MetLife: reviewer needs updated X-ray. Uploading to portal immediately" },
    { type: "call", text: "Cigna: $450 filling, 91 days, status 'Paid' but payment not received — requesting EFT trace" },
    { type: "action", text: "Batch-submitting status inquiries for remaining 6 claims via payer portals" },
    { type: "result", text: "8 high-value claims worked: 2 resolved, 3 escalated, 3 inquiries filed — $6,200 actively pursued" },
  ]},
  { agent: "AI AR & Collections", text: "Catch Underpayments vs. Contracted Rates", workflow: [
    { type: "check", text: "Scanning this week's 47 EOBs against contracted fee schedules" },
    { type: "check", text: "Claim #38: prophy billed $145, contracted $145, paid $130. Underpaid by $15" },
    { type: "warn", text: "Claim #41: composite $245, paid $198 — amalgam downgrade applied" },
    { type: "reason", text: "Checking plan document... downgrade clause was REMOVED in 2024 renewal. This is a payer error" },
    { type: "action", text: "Submitting reprocessing request with contracted rate documentation" },
    { type: "action", text: "Submitting appeal with updated plan document showing no downgrade clause" },
    { type: "result", text: "2 underpayments caught: $15 + $47 = $62 recovered. Most offices miss these entirely" },
  ]},

  // ── AI EOB Posting ──
  { agent: "AI EOB Posting", text: "Post EOBs & Reconcile Insurance Payments", workflow: [
    { type: "action", text: "ERA received from Delta Dental — matching to claim CLM-2024-89471" },
    { type: "check", text: "D2740 crown: allowed $1,180, paid $590 (50%), patient responsibility $590 — matches estimate" },
    { type: "check", text: "D2950 buildup: allowed $285, paid $228 (80% basic), patient responsibility $57" },
    { type: "warn", text: "Buildup classified as 'Basic' (80%) not 'Major' (50%) — patient owes LESS than estimated" },
    { type: "action", text: "Posting $818 to ledger. Patient overpaid $57 — creating credit on account" },
    { type: "result", text: "EOB posted, payments matched, credit identified — ledger balanced to the penny" },
  ]},
  { agent: "AI EOB Posting", text: "Handle Scanned PDFs & Catch Payer Errors", workflow: [
    { type: "check", text: "Cigna EOB arrived as scanned fax — OCR extracting 6 service lines..." },
    { type: "warn", text: "OCR failure on 2 lines: \"$8_.00\" and \"$14_.00\" — amounts unreadable" },
    { type: "reason", text: "Cross-referencing contracted fee schedule: D1110 prophy = $89, D4341 SRP = $142. Resolved." },
    { type: "warn", text: "Unmatched line: -$215 recoupment for 'Michael Chen' — patient not in our system" },
    { type: "check", text: "Tracing: 8-month-old claim where Cigna overpaid because Michael had Aetna as primary" },
    { type: "action", text: "Recoupment valid — reversing original overpayment, patient now owes $37 to Aetna" },
    { type: "result", text: "Scanned EOB parsed, OCR gaps resolved, mystery recoupment traced and reconciled — all autonomous" },
  ]},

  // ── AI Scribe ──
  { agent: "AI Scribe", text: "Real-Time Clinical Transcription", workflow: [
    { type: "action", text: "Listening to Dr. Smith during composite filling — transcribing in real-time" },
    { type: "action", text: "Capturing: \"Anesthesia: 2 carpules 2% lidocaine 1:100K epi, IA block + long buccal\"" },
    { type: "action", text: "Recording: \"Existing MOD amalgam removed, caries excavated, no pulp exposure, buildup placed\"" },
    { type: "check", text: "Noting vitality test results, percussion assessment, BOP findings on adjacent teeth" },
    { type: "action", text: "Extracting clinical entities: tooth #19, distal-occlusal surfaces, Class II composite" },
    { type: "result", text: "Complete clinical narrative captured from voice — provider never touched a keyboard" },
  ]},
  { agent: "AI Scribe", text: "Auto-Generate CDT Codes & SOAP Notes", workflow: [
    { type: "reason", text: "Mapping dictated procedure to CDT: \"two-surface composite on #19\" = D2392" },
    { type: "action", text: "Generating structured SOAP note: Chief Complaint, Clinical Findings, Treatment, Post-Op" },
    { type: "check", text: "Cross-referencing payer bundling rules — D2392 + D0220 not bundled by this carrier. OK" },
    { type: "action", text: "Attaching clinical photos and periapical X-ray to the note automatically" },
    { type: "file", text: "Note finalized: coded D2392 + D0220, SOAP structured, photos linked, ready for claim generation" },
    { type: "result", text: "CDT codes mapped, SOAP note generated, compliance checked — claim-ready before patient leaves the chair" },
  ]},

  // ── AI Treatment Planner ──
  { agent: "AI Treatment Planner", text: "Generate Insurance-Aware Treatment Plans", workflow: [
    { type: "check", text: "Analyzing findings: fractured cusp #14, 5mm pockets lower left, overdue hygiene, recurrent decay #30" },
    { type: "reason", text: "Prioritizing by clinical urgency: crown #14 (urgent), SRP lower left (needed), prophy (routine)" },
    { type: "action", text: "Generating treatment plan with CDT codes, sequencing, and estimated timelines" },
    { type: "check", text: "Running insurance estimates: crown at 50% ($590 OOP), SRP at 80% ($178 OOP)" },
    { type: "reason", text: "Benefit optimization: crown now while $1,180 remaining. SRP in January when benefits reset" },
    { type: "result", text: "23-procedure plan generated with clinical priority, insurance math, and optimal sequencing" },
  ]},
  { agent: "AI Treatment Planner", text: "Create Patient-Friendly Visual Explanations", workflow: [
    { type: "action", text: "Translating CDT codes to plain language — \"D2740\" becomes \"porcelain crown to protect your cracked tooth\"" },
    { type: "action", text: "Creating visual pocket depth diagram: healthy (2mm) vs. moderate (4mm) vs. deep (6mm)" },
    { type: "action", text: "Generating patient narrative: \"Your gums show early signs of disease in the lower left. A deep cleaning will remove bacteria below the gumline...\"" },
    { type: "check", text: "Calculating clear financial breakdown — insurance portion, patient portion, payment options" },
    { type: "action", text: "Presenting phased approach: \"Start with the crown (most urgent), then address gum health in January\"" },
    { type: "result", text: "Patient sees exactly what's wrong, why it matters, what it costs — case acceptance up 34%" },
  ]},

  // ── AI Copilot ──
  { agent: "AI Copilot", text: "Answer Natural Language Practice Questions", workflow: [
    { type: "call", text: "User asks: \"Why did Robert Johnson's SRP claim get denied?\"" },
    { type: "check", text: "Loading Robert's claim history, EOBs, clinical notes, perio charts..." },
    { type: "reason", text: "Denial code CO-50 M127: 'Missing/incomplete/invalid documentation.' Checking what was sent..." },
    { type: "check", text: "Clinical notes show 14 sites with 5-6mm pockets + BOP — but perio chart wasn't attached to claim" },
    { type: "reason", text: "This is winnable on appeal. Perio data supports medical necessity — just need to attach the chart" },
    { type: "result", text: "Root cause identified: missing perio chart attachment. Recommendation: APPEAL with 85% confidence" },
  ]},
  { agent: "AI Copilot", text: "Analyze Revenue Cycle Issues Across Sources", workflow: [
    { type: "call", text: "User asks: \"Analyze my EOBs from the past month — what should I change about my revenue cycle?\"" },
    { type: "check", text: "Loading all EOBs from past 30 days — 47 claims across 6 payers" },
    { type: "reason", text: "Pattern: MetLife denial rate jumped to 12% (vs. 4% average). All denials are CO-11: diagnosis inconsistent" },
    { type: "check", text: "Cross-referencing: MetLife updated their ICD-10 requirements last month — we're still using old codes" },
    { type: "warn", text: "Also found: 3 Aetna claims underpaid by $15-47 each — amalgam downgrade applied on plans without it" },
    { type: "action", text: "Recommendations: (1) Update ICD-10 codes for MetLife submissions, (2) Appeal 3 Aetna underpayments" },
    { type: "result", text: "Revenue cycle issues identified with root causes and specific fixes — not just data, actionable insights" },
  ]},

  // ── AI Revenue Analytics ──
  { agent: "AI Revenue Analytics", text: "Real-Time Production & Collections Dashboard", workflow: [
    { type: "check", text: "MTD gross production: $1,023,690 (102% of goal). Net production: adjustment ratio 133%" },
    { type: "check", text: "Collections: $892,400 (87% collection rate). AR aging: $42,300 — 60% in 0-30 day bucket" },
    { type: "check", text: "Case acceptance: 68% (↑3% vs. last month). Hygiene recare: 74% (↓2%)" },
    { type: "action", text: "Provider breakdown: Dr. Patel $89K production, Dr. Kim $112K — Kim outperforming by 26%" },
    { type: "warn", text: "Hygiene recare dropped 2% — recommending increased recall outreach campaign" },
    { type: "result", text: "Full practice health visible in real-time: production, collections, AR, KPIs — replaces Dental Intel" },
  ]},
  { agent: "AI Revenue Analytics", text: "AI-Powered Insights & Trend Detection", workflow: [
    { type: "reason", text: "Analyzing 6-month revenue trends — identifying growth drivers and risk areas" },
    { type: "check", text: "Implant revenue up 34% QoQ — Dr. Kim's CEREC cases driving growth" },
    { type: "check", text: "Hygiene production flat despite 2 open hygiene days per week — scheduling gap identified" },
    { type: "warn", text: "Denial rate trending up: 4.2% → 5.8% over 3 months — primarily MetLife claims" },
    { type: "action", text: "Recommendation: audit MetLife coding process. Likely modifier requirement change" },
    { type: "action", text: "Forecasting: $310K next quarter if current trends hold. $340K with recommended fixes = $30K upside" },
    { type: "result", text: "Actionable insights: growth drivers, risk areas, and $30K revenue opportunity identified" },
  ]},

  // ── AI Pre-Authorization ──
  { agent: "AI Insurance Verification", text: "Submit & Track Pre-Authorizations", workflow: [
    { type: "reason", text: "Crown D2740 on #14 requires pre-auth. Compiling supporting documentation..." },
    { type: "file", text: "Generating narrative: \"Tooth #14 fractured DL cusp, existing MOD amalgam compromising structure...\"" },
    { type: "action", text: "Attaching periapical radiograph, pre-op photo, clinical notes" },
    { type: "send", text: "Submitting electronically — tracking number PA-2024-48291" },
    { type: "wait", text: "Monitoring daily. Day 8: still pending — calling payer to check status" },
    { type: "warn", text: "Reviewer requested additional intraoral photo. Uploading same day with cover letter" },
    { type: "check", text: "Day 10: Pre-auth APPROVED. Authorized: $1,180. Patient OOP: $590" },
    { type: "result", text: "Pre-auth secured after proactive follow-up — caught delay and resolved autonomously" },
  ]},

  // ── Year-End Benefits ──
  { agent: "AI Case Acceptance", text: "Year-End Benefits Maximization Campaign", workflow: [
    { type: "check", text: "Running benefits analysis across 2,400 patients — benefits reset Jan 1" },
    { type: "action", text: "340 patients with $500+ remaining benefits AND recommended treatment on file" },
    { type: "reason", text: "Top segment: 47 patients with $1,000+ remaining — leaving money on the table" },
    { type: "sms", text: "\"You have $[amount] in dental benefits expiring Dec 31. Don't lose them! Book now: [link]\"" },
    { type: "call", text: "Calling top 10 highest-value: \"Mr. Chen, your crown would cost just $220 OOP before year-end\"" },
    { type: "result", text: "47 patients contacted, 18 scheduled — $32,000 in production booked from expiring benefits" },
  ]},
];

const WorkflowLine = ({ item, index }) => {
  const Icon = typeIcon[item.type] || CheckCircle2;
  const style = typeStyle[item.type] || "text-slate-600";
  const isBlock = ["reason", "call", "sms", "warn", "result"].includes(item.type);

  if (isBlock) {
    return (
      <motion.div
        initial={{ opacity: 0, x: -6 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: index * 0.03, duration: 0.2 }}
        className={`rounded-lg border px-3 py-2 ${style}`}
      >
        <div className="flex items-start gap-2">
          <Icon className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 opacity-70" />
          <span className="text-[11px] leading-relaxed">{item.text}</span>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: -6 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.03, duration: 0.2 }}
      className="flex items-start gap-2 px-1"
    >
      <Icon className="w-3 h-3 flex-shrink-0 mt-0.5 text-slate-400" />
      <span className={`text-[11px] leading-relaxed ${style}`}>{item.text}</span>
    </motion.div>
  );
};

/* ─── Rotating color palette for visual variety ─── */
const pillColors = [
  { bg: "bg-blue-50", hoverBg: "bg-blue-100", border: "border-blue-200", dot: "bg-blue-500", accent: "#3b82f6" },
  { bg: "bg-indigo-50", hoverBg: "bg-indigo-100", border: "border-indigo-200", dot: "bg-indigo-500", accent: "#6366f1" },
  { bg: "bg-purple-50", hoverBg: "bg-purple-100", border: "border-purple-200", dot: "bg-purple-500", accent: "#a855f7" },
  { bg: "bg-emerald-50", hoverBg: "bg-emerald-100", border: "border-emerald-200", dot: "bg-emerald-500", accent: "#10b981" },
  { bg: "bg-red-50", hoverBg: "bg-red-100", border: "border-red-200", dot: "bg-red-500", accent: "#ef4444" },
  { bg: "bg-amber-50", hoverBg: "bg-amber-100", border: "border-amber-200", dot: "bg-amber-500", accent: "#f59e0b" },
];

const Popover = ({ func, colors, anchorRef, onMouseEnter, onMouseLeave }) => {
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!anchorRef?.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const popW = 380;
    const popH = 420;
    const spaceBelow = window.innerHeight - rect.bottom;
    const top = spaceBelow > popH + 8
      ? rect.bottom + 6
      : Math.max(8, rect.top - popH - 6);
    const left = Math.min(rect.left, window.innerWidth - popW - 12);
    setPos({ top, left: Math.max(8, left) });
  }, [anchorRef]);

  return ReactDOM.createPortal(
    <motion.div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      initial={{ opacity: 0, y: 6, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 6, scale: 0.96 }}
      transition={{ duration: 0.15 }}
      style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 9999 }}
      className="w-[380px] bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden"
    >
      <div className={`px-4 py-2.5 ${colors.bg} border-b ${colors.border} flex items-center gap-2`}>
        <BrainCircuit className="w-3.5 h-3.5" style={{ color: colors.accent }} />
        <span className="text-[12px] font-bold text-slate-800 flex-1">{func.text}</span>
        <span className="text-[9px] font-bold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: colors.accent }}>
          {func.workflow.length} steps
        </span>
      </div>
      <div className="px-3 py-2.5 max-h-[360px] overflow-y-auto space-y-1.5">
        {func.workflow.map((item, i) => (
          <WorkflowLine key={i} item={item} index={i} />
        ))}
      </div>
    </motion.div>,
    document.body
  );
};

const FunctionPill = ({ func, colors }) => {
  const [isOpen, setIsOpen] = useState(false);
  const btnRef = useRef(null);
  const timeoutRef = useRef(null);

  const handleMouseEnter = () => {
    clearTimeout(timeoutRef.current);
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setIsOpen(false), 200);
  };

  return (
    <>
      <div
        ref={btnRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border transition-all duration-200 text-left cursor-default ${
          isOpen
            ? `${colors.bg} ${colors.border} shadow-sm`
            : `${colors.bg} hover:${colors.hoverBg} border-transparent`
        }`}
      >
        <span className="text-[12px] font-medium text-slate-700 whitespace-nowrap">
          {func.text}
        </span>
        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
          isOpen
            ? `${colors.dot} text-white`
            : "bg-slate-200/80 text-slate-500"
        }`}>
          {func.workflow.length}
        </span>
      </div>
      <AnimatePresence>
        {isOpen && (
          <Popover
            func={func}
            colors={colors}
            anchorRef={btnRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          />
        )}
      </AnimatePresence>
    </>
  );
};

const AIFunctionsSlide = ({ hideTag = false }) => {
  const totalFunctions = allFunctions.length;
  const totalSteps = allFunctions.reduce((acc, f) => acc + f.workflow.length, 0);

  return (
    <div className="h-full w-full bg-slate-50 flex flex-col p-5 lg:p-6 relative overflow-hidden font-sans">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-100 rounded-full blur-[120px] opacity-40 translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-50 rounded-full blur-[100px] opacity-40 -translate-x-1/2 translate-y-1/2" />

      <div className="relative z-10 flex flex-col h-full min-h-0">
        {/* Header */}
        <div className="mb-2 flex-shrink-0">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
            {!hideTag && (
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-sm font-semibold mb-2 border border-blue-100">
                <Users className="w-4 h-4" />
                <span>The Patient Journey</span>
              </div>
            )}
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-tight">
              Asha's <span className="text-blue-600">AI</span> performs the{" "}
              <span className="text-blue-600">work/labor</span> that takes your
              staff 3-4 hours per patient
            </h2>
            <p className="text-base text-slate-600 leading-relaxed mt-2 mb-3">
              You pay $120+ per patient journey, but work still falls through.
              AI handles all of it — accurately, 24/7. Hover any step to see the
              reasoning and actions.
            </p>
          </motion.div>
        </div>

        {/* Flat tile grid — no categories, rotating colors */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="flex flex-wrap gap-2.5">
            {allFunctions.map((func, i) => (
              <FunctionPill key={i} func={func} colors={pillColors[i % pillColors.length]} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export { allFunctions, pillColors };
export default AIFunctionsSlide;
